import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';

/**
 * Component that tracks the current route and saves it to sessionStorage
 * Enhanced to prevent overwriting important routes during redirects
 * Both authentication redirects and route persistence are now supported.
 */
const RouteTracker = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { loading: permissionLoading } = usePermission();

  useEffect(() => {
    // Only track routes when authenticated and permissions are fully loaded
    // This prevents overwriting routes during permission-based redirects
    if (isAuthenticated && location.pathname && !permissionLoading) {
      // Don't save the root path (/) which is often a redirect destination
      if (location.pathname === '/') {
        console.log('RouteTracker: Root path detected, not overwriting stored routes');
        return;
      }
      
      // Don't save auth-related pages that shouldn't be restored on refresh
      if (location.pathname === '/login' || location.pathname === '/register') {
        console.log('RouteTracker: Auth page detected, not saving route');
        return;
      }
      
      // Special handling for "restoration in progress" flag
      // This prevents saving temporary routes during route restoration
      const isRestoring = sessionStorage.getItem('routeRestorationInProgress') === 'true';
      if (isRestoring) {
        console.log('RouteTracker: Route restoration in progress, skipping route save');
        return;
      }
      
      console.log('RouteTracker: Saving current path:', location.pathname);
      sessionStorage.setItem('last_path', location.pathname);
      sessionStorage.setItem('lastRoute', location.pathname);
      
      // Also update timestamp to track when this route was recorded
      sessionStorage.setItem('routeTimestamp', Date.now().toString());
    }
  }, [location, isAuthenticated, permissionLoading]);

  // This is a utility component that doesn't render anything
  return null;
};

export default RouteTracker;